/**
 * ContractsTab — Phase 6a.17 per Frontend Spec §21.8.6.
 *
 * The 6th and final results tab. Surfaces
 * `aggregations.contractSummaries` as a Big4 auditor working-paper data
 * grid: sticky SummaryCard, toolbar with 4 filters + column visibility +
 * clear-filters, single shadcn `<Table>` with sortable headers, per-row
 * 5-emoji flag badges, per-row Detay button (Phase 6a.18 toast hook),
 * TOPLAM footer aggregating the filtered set, and a flag legend strip.
 *
 * Key non-negotiables (Spec §21.8.6):
 *   • Single shadcn `<Table>` only — no external data-grid library.
 *   • 3 pinned columns: index, contractId, detail (visibility disabled).
 *   • Search is OR across contractId + description, case-insensitive.
 *   • Filters compose with AND logic.
 *   • Single-column sort; default `index` ASC; flags column unsortable.
 *   • Functional-currency only (native currency drill = Phase 6a.18).
 *   • Asset class TR labels reuse `form.assetClass.*` from Phase 6c.2.
 */

import { format, parseISO } from "date-fns";
import { ChevronDown, ChevronUp, ExternalLink, Search, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useOutletContext } from "react-router-dom";

import { buildContractDetailPath } from "@/pages/engagement/utils/contractDetailLink";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatCurrency, formatSigned } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  ASSET_CLASS_ORDER,
  type AssetClass,
  type ContractSummary,
  type EngagementHistoryEntry,
} from "@/types";

interface OutletContext {
  engagement: EngagementHistoryEntry;
}

type SortKey =
  | "index"
  | "contractId"
  | "description"
  | "assetClass"
  | "commencementDate"
  | "termMonths"
  | "currency"
  | "rouAssetClosing"
  | "leaseLiabilityClosing";
type SortDir = "asc" | "desc";

type ComplexityFilter =
  | "ALL"
  | "SIMPLE"
  | "COMPLEX"
  | "MULTI_CURRENCY"
  | "MODIFICATION"
  | "TERMINATION"
  | "INDEX_LINKAGE"
  | "PRIOR_RECONCILIATION";

const ALL_COLUMN_KEYS = [
  "index",
  "contractId",
  "description",
  "assetClass",
  "commencementDate",
  "termMonths",
  "currency",
  "rouAssetClosing",
  "leaseLiabilityClosing",
  "flags",
  "detail",
] as const;
type ColumnKey = (typeof ALL_COLUMN_KEYS)[number];

const PINNED_COLUMNS: ReadonlySet<ColumnKey> = new Set([
  "index",
  "contractId",
  "detail",
]);

const DEFAULT_VISIBLE: Record<ColumnKey, boolean> = {
  index: true,
  contractId: true,
  description: true,
  assetClass: true,
  commencementDate: true,
  termMonths: true,
  currency: true,
  rouAssetClosing: true,
  leaseLiabilityClosing: true,
  flags: true,
  detail: true,
};

function hasAnyFlag(c: ContractSummary): boolean {
  return (
    c.hasModifications ||
    c.hasTermination ||
    c.hasIndexLinkage ||
    c.isMultiCurrency ||
    c.hasPriorReconciliation
  );
}

export function ContractsTab() {
  const { t } = useTranslation();
  const { engagement } = useOutletContext<OutletContext>();
  const contracts = engagement.lastResult?.aggregations.contractSummaries ?? [];
  const currency = engagement.functionalCurrency;

  const [searchQuery, setSearchQuery] = useState("");
  const [assetClassFilter, setAssetClassFilter] = useState<AssetClass | "ALL">(
    "ALL",
  );
  const [currencyFilter, setCurrencyFilter] = useState<string>("ALL");
  const [complexityFilter, setComplexityFilter] =
    useState<ComplexityFilter>("ALL");
  const [sortKey, setSortKey] = useState<SortKey>("index");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [visibleColumns, setVisibleColumns] =
    useState<Record<ColumnKey, boolean>>(DEFAULT_VISIBLE);

  const uniqueCurrencies = useMemo(
    () => Array.from(new Set(contracts.map((c) => c.currency))).sort(),
    [contracts],
  );

  // ── Filter (AND across search × assetClass × currency × complexity) ────
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return contracts.filter((c) => {
      if (q) {
        const inId = c.contractId.toLowerCase().includes(q);
        const inDesc = (c.description ?? "").toLowerCase().includes(q);
        if (!inId && !inDesc) return false;
      }
      if (assetClassFilter !== "ALL" && c.assetClass !== assetClassFilter) {
        return false;
      }
      if (currencyFilter !== "ALL" && c.currency !== currencyFilter) {
        return false;
      }
      switch (complexityFilter) {
        case "ALL":
          break;
        case "SIMPLE":
          if (hasAnyFlag(c)) return false;
          break;
        case "COMPLEX":
          if (!hasAnyFlag(c)) return false;
          break;
        case "MULTI_CURRENCY":
          if (!c.isMultiCurrency) return false;
          break;
        case "MODIFICATION":
          if (!c.hasModifications) return false;
          break;
        case "TERMINATION":
          if (!c.hasTermination) return false;
          break;
        case "INDEX_LINKAGE":
          if (!c.hasIndexLinkage) return false;
          break;
        case "PRIOR_RECONCILIATION":
          if (!c.hasPriorReconciliation) return false;
          break;
      }
      return true;
    });
  }, [
    contracts,
    searchQuery,
    assetClassFilter,
    currencyFilter,
    complexityFilter,
  ]);

  // ── Sort (single-column, TR-locale aware) ───────────────────────────────
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case "index":
          cmp = a.contractIndex - b.contractIndex;
          break;
        case "contractId":
          cmp = a.contractId.localeCompare(b.contractId, "tr");
          break;
        case "description":
          cmp = (a.description ?? "").localeCompare(
            b.description ?? "",
            "tr",
          );
          break;
        case "assetClass": {
          const al = t(`form.assetClass.${a.assetClass}` as never) as string;
          const bl = t(`form.assetClass.${b.assetClass}` as never) as string;
          cmp = al.localeCompare(bl, "tr");
          break;
        }
        case "commencementDate":
          cmp =
            parseISO(a.commencementDate).getTime() -
            parseISO(b.commencementDate).getTime();
          break;
        case "termMonths":
          cmp = a.termMonths - b.termMonths;
          break;
        case "currency":
          cmp = a.currency.localeCompare(b.currency);
          break;
        case "rouAssetClosing":
          cmp = Number(a.rouAssetClosing) - Number(b.rouAssetClosing);
          break;
        case "leaseLiabilityClosing":
          cmp =
            Number(a.leaseLiabilityClosing) - Number(b.leaseLiabilityClosing);
          break;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortKey, sortDir, t]);

  // ── Aggregates (computed from filtered, surfaced in Summary + Footer) ──
  const totals = useMemo(() => {
    let rou = 0;
    let liab = 0;
    let simple = 0;
    for (const c of filtered) {
      rou += Number(c.rouAssetClosing) || 0;
      liab += Number(c.leaseLiabilityClosing) || 0;
      if (!hasAnyFlag(c)) simple += 1;
    }
    return {
      rouTotal: rou,
      liabilityTotal: liab,
      netPosition: rou - liab,
      simpleCount: simple,
      complexCount: filtered.length - simple,
    };
  }, [filtered]);

  const isFiltered =
    searchQuery !== "" ||
    assetClassFilter !== "ALL" ||
    currencyFilter !== "ALL" ||
    complexityFilter !== "ALL";

  function clearFilters() {
    setSearchQuery("");
    setAssetClassFilter("ALL");
    setCurrencyFilter("ALL");
    setComplexityFilter("ALL");
  }

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function toggleColumn(key: ColumnKey, visible: boolean) {
    if (PINNED_COLUMNS.has(key)) return;
    setVisibleColumns((prev) => ({ ...prev, [key]: visible }));
  }

  if (contracts.length === 0) {
    return (
      <Card className="p-8" data-testid="contracts-empty">
        <p className="text-center italic text-muted-foreground">
          {t("contracts.emptyState.noContracts")}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <SummaryCard
        totalCount={contracts.length}
        filteredCount={filtered.length}
        totals={totals}
        currency={currency}
        isFiltered={isFiltered}
      />

      <Toolbar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        assetClassFilter={assetClassFilter}
        onAssetClassChange={setAssetClassFilter}
        currencyFilter={currencyFilter}
        onCurrencyChange={setCurrencyFilter}
        uniqueCurrencies={uniqueCurrencies}
        complexityFilter={complexityFilter}
        onComplexityChange={setComplexityFilter}
        visibleColumns={visibleColumns}
        onToggleColumn={toggleColumn}
        isFiltered={isFiltered}
        onClearFilters={clearFilters}
      />

      {sorted.length === 0 ? (
        <Card className="p-8" data-testid="contracts-empty-filtered">
          <p className="text-center italic text-muted-foreground">
            {t("contracts.emptyState.filtered")}
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto w-full">
            <Table className="w-full" data-testid="contracts-table">
            <TableHeader>
              <TableRow>
                {visibleColumns.index && (
                  <SortableHeader
                    sortKey="index"
                    currentSort={sortKey}
                    dir={sortDir}
                    onClick={toggleSort}
                    label={t("contracts.column.index")}
                  />
                )}
                {visibleColumns.contractId && (
                  <SortableHeader
                    sortKey="contractId"
                    currentSort={sortKey}
                    dir={sortDir}
                    onClick={toggleSort}
                    label={t("contracts.column.contractId")}
                  />
                )}
                {visibleColumns.description && (
                  <SortableHeader
                    sortKey="description"
                    currentSort={sortKey}
                    dir={sortDir}
                    onClick={toggleSort}
                    label={t("contracts.column.description")}
                  />
                )}
                {visibleColumns.assetClass && (
                  <SortableHeader
                    sortKey="assetClass"
                    currentSort={sortKey}
                    dir={sortDir}
                    onClick={toggleSort}
                    label={t("contracts.column.assetClass")}
                  />
                )}
                {visibleColumns.commencementDate && (
                  <SortableHeader
                    sortKey="commencementDate"
                    currentSort={sortKey}
                    dir={sortDir}
                    onClick={toggleSort}
                    label={t("contracts.column.commencementDate")}
                  />
                )}
                {visibleColumns.termMonths && (
                  <SortableHeader
                    sortKey="termMonths"
                    currentSort={sortKey}
                    dir={sortDir}
                    onClick={toggleSort}
                    label={t("contracts.column.termMonths")}
                  />
                )}
                {visibleColumns.currency && (
                  <SortableHeader
                    sortKey="currency"
                    currentSort={sortKey}
                    dir={sortDir}
                    onClick={toggleSort}
                    label={t("contracts.column.currency")}
                  />
                )}
                {visibleColumns.rouAssetClosing && (
                  <SortableHeader
                    sortKey="rouAssetClosing"
                    currentSort={sortKey}
                    dir={sortDir}
                    onClick={toggleSort}
                    label={t("contracts.column.rouAsset")}
                    alignRight
                  />
                )}
                {visibleColumns.leaseLiabilityClosing && (
                  <SortableHeader
                    sortKey="leaseLiabilityClosing"
                    currentSort={sortKey}
                    dir={sortDir}
                    onClick={toggleSort}
                    label={t("contracts.column.leaseLiability")}
                    alignRight
                  />
                )}
                {visibleColumns.flags && (
                  <TableHead data-testid="contracts-header-flags">
                    {t("contracts.column.flags")}
                  </TableHead>
                )}
                {visibleColumns.detail && (
                  <TableHead
                    className="w-10"
                    data-testid="contracts-header-detail"
                  >
                    {/* Phase BB5-7 — icon column, header intentionally empty */}
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((c) => (
                <ContractRow
                  key={c.contractIndex}
                  contract={c}
                  visibleColumns={visibleColumns}
                  currency={currency}
                  engagementId={engagement.id}
                />
              ))}
            </TableBody>
            <TableFooter>
              <TableRow
                className="font-bold border-t-2 bg-muted/30"
                data-testid="contracts-footer"
              >
                {visibleColumns.index && (
                  <TableCell>
                    {t("contracts.row.totalCount", { count: filtered.length })}
                  </TableCell>
                )}
                {visibleColumns.contractId && (
                  <TableCell>{t("contracts.row.total")}</TableCell>
                )}
                {visibleColumns.description && <TableCell />}
                {visibleColumns.assetClass && <TableCell />}
                {visibleColumns.commencementDate && <TableCell />}
                {visibleColumns.termMonths && <TableCell />}
                {visibleColumns.currency && <TableCell />}
                {visibleColumns.rouAssetClosing && (
                  <TableCell
                    className="text-right font-mono tabular-nums"
                    data-testid="contracts-footer-rou"
                  >
                    {formatCurrency(totals.rouTotal.toFixed(2), currency)}
                  </TableCell>
                )}
                {visibleColumns.leaseLiabilityClosing && (
                  <TableCell
                    className="text-right font-mono tabular-nums"
                    data-testid="contracts-footer-liability"
                  >
                    {formatCurrency(
                      totals.liabilityTotal.toFixed(2),
                      currency,
                    )}
                  </TableCell>
                )}
                {visibleColumns.flags && <TableCell />}
                {visibleColumns.detail && <TableCell />}
              </TableRow>
            </TableFooter>
            </Table>
          </div>
        </Card>
      )}

      <p
        className="text-xs text-muted-foreground flex flex-wrap gap-3 mt-2"
        data-testid="contracts-legend"
      >
        {t("contracts.legend.text")}
      </p>
    </div>
  );
}

// ─── SummaryCard ───────────────────────────────────────────────────────────

interface SummaryCardProps {
  totalCount: number;
  filteredCount: number;
  totals: {
    rouTotal: number;
    liabilityTotal: number;
    netPosition: number;
    simpleCount: number;
    complexCount: number;
  };
  currency: string;
  isFiltered: boolean;
}

function SummaryCard({
  totalCount,
  filteredCount,
  totals,
  currency,
  isFiltered,
}: SummaryCardProps) {
  const { t } = useTranslation();
  const netIsNegative = totals.netPosition < 0;
  return (
    <Card
      className="p-4 bg-muted/30 sticky top-0 z-10 backdrop-blur"
      data-testid="contracts-summary"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
        <h3 className="text-base font-semibold">
          {t("contracts.section.title")}
        </h3>
        <p
          className="text-sm text-muted-foreground"
          data-testid="contracts-summary-count"
        >
          {isFiltered
            ? t("contracts.summary.filteredCount", {
                filtered: filteredCount,
                total: totalCount,
              })
            : t("contracts.summary.totalCount", { count: totalCount })}
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-1 text-sm">
        <div data-testid="contracts-summary-rou">
          <span className="text-muted-foreground">
            {t("contracts.summary.rouTotal")}:
          </span>{" "}
          <span className="font-mono tabular-nums font-semibold">
            {formatCurrency(totals.rouTotal.toFixed(2), currency)}
          </span>
        </div>
        <div data-testid="contracts-summary-liability">
          <span className="text-muted-foreground">
            {t("contracts.summary.liabilityTotal")}:
          </span>{" "}
          <span className="font-mono tabular-nums font-semibold">
            {formatCurrency(totals.liabilityTotal.toFixed(2), currency)}
          </span>
        </div>
        <div data-testid="contracts-summary-net">
          <span className="text-muted-foreground">
            {t("contracts.summary.netPosition")}:
          </span>{" "}
          <span
            className={cn(
              "font-mono tabular-nums font-semibold",
              netIsNegative && "text-destructive",
            )}
          >
            {formatSigned(totals.netPosition.toFixed(2), currency)}
          </span>
        </div>
        <div data-testid="contracts-summary-complexity">
          <span className="text-muted-foreground">
            {t("contracts.summary.complexity", {
              simple: totals.simpleCount,
              complex: totals.complexCount,
            })}
          </span>
        </div>
      </div>
    </Card>
  );
}

// ─── Toolbar ───────────────────────────────────────────────────────────────

interface ToolbarProps {
  searchQuery: string;
  onSearchChange: (v: string) => void;
  assetClassFilter: AssetClass | "ALL";
  onAssetClassChange: (v: AssetClass | "ALL") => void;
  currencyFilter: string;
  onCurrencyChange: (v: string) => void;
  uniqueCurrencies: string[];
  complexityFilter: ComplexityFilter;
  onComplexityChange: (v: ComplexityFilter) => void;
  visibleColumns: Record<ColumnKey, boolean>;
  onToggleColumn: (key: ColumnKey, visible: boolean) => void;
  isFiltered: boolean;
  onClearFilters: () => void;
}

function Toolbar({
  searchQuery,
  onSearchChange,
  assetClassFilter,
  onAssetClassChange,
  currencyFilter,
  onCurrencyChange,
  uniqueCurrencies,
  complexityFilter,
  onComplexityChange,
  visibleColumns,
  onToggleColumn,
  isFiltered,
  onClearFilters,
}: ToolbarProps) {
  const { t } = useTranslation();
  return (
    <Card className="p-4" data-testid="contracts-toolbar">
      <div className="flex flex-wrap gap-3 items-end">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("contracts.toolbar.search.placeholder")}
            className="pl-8 w-[260px]"
            data-testid="contracts-search"
          />
        </div>

        <div>
          <label className="block text-xs font-medium mb-1 text-muted-foreground">
            {t("contracts.toolbar.assetClass.label")}
          </label>
          <Select
            value={assetClassFilter}
            onValueChange={(v) =>
              onAssetClassChange(v as AssetClass | "ALL")
            }
          >
            <SelectTrigger
              className="w-[160px]"
              data-testid="contracts-filter-assetClass"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">
                {t("contracts.toolbar.assetClass.all")}
              </SelectItem>
              {ASSET_CLASS_ORDER.map((ac) => (
                <SelectItem key={ac} value={ac}>
                  {t(`form.assetClass.${ac}` as never) as string}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-xs font-medium mb-1 text-muted-foreground">
            {t("contracts.toolbar.currency.label")}
          </label>
          <Select
            value={currencyFilter}
            onValueChange={(v) => onCurrencyChange(v)}
          >
            <SelectTrigger
              className="w-[120px]"
              data-testid="contracts-filter-currency"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">
                {t("contracts.toolbar.currency.all")}
              </SelectItem>
              {uniqueCurrencies.map((cur) => (
                <SelectItem key={cur} value={cur}>
                  {cur}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-xs font-medium mb-1 text-muted-foreground">
            {t("contracts.toolbar.complexity.label")}
          </label>
          <Select
            value={complexityFilter}
            onValueChange={(v) => onComplexityChange(v as ComplexityFilter)}
          >
            <SelectTrigger
              className="w-[220px]"
              data-testid="contracts-filter-complexity"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">
                {t("contracts.toolbar.complexity.all")}
              </SelectItem>
              <SelectItem value="SIMPLE">
                {t("contracts.toolbar.complexity.simple")}
              </SelectItem>
              <SelectItem value="COMPLEX">
                {t("contracts.toolbar.complexity.complex")}
              </SelectItem>
              <SelectItem value="MULTI_CURRENCY">
                {t("contracts.toolbar.complexity.multiCurrency")}
              </SelectItem>
              <SelectItem value="MODIFICATION">
                {t("contracts.toolbar.complexity.modification")}
              </SelectItem>
              <SelectItem value="TERMINATION">
                {t("contracts.toolbar.complexity.termination")}
              </SelectItem>
              <SelectItem value="INDEX_LINKAGE">
                {t("contracts.toolbar.complexity.indexLinkage")}
              </SelectItem>
              <SelectItem value="PRIOR_RECONCILIATION">
                {t("contracts.toolbar.complexity.priorReconciliation")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                data-testid="contracts-columns-trigger"
              >
                {t("contracts.toolbar.columns")}
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>
                {t("contracts.toolbar.columns")}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {ALL_COLUMN_KEYS.map((key) => (
                <DropdownMenuCheckboxItem
                  key={key}
                  checked={visibleColumns[key]}
                  disabled={PINNED_COLUMNS.has(key)}
                  onCheckedChange={(c) => onToggleColumn(key, c === true)}
                  data-testid={`contracts-column-toggle-${key}`}
                  onSelect={(e) => e.preventDefault()}
                >
                  {t(`contracts.column.${columnKeyToLabelKey(key)}` as never)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {isFiltered && (
          <Button
            variant="ghost"
            onClick={onClearFilters}
            data-testid="contracts-clear-filters"
          >
            <X className="mr-1 h-4 w-4" />
            {t("contracts.toolbar.clearFilters")}
          </Button>
        )}
      </div>
    </Card>
  );
}

/** Map a column key to the i18n label key suffix under `contracts.column.*`. */
function columnKeyToLabelKey(key: ColumnKey): string {
  switch (key) {
    case "rouAssetClosing":
      return "rouAsset";
    case "leaseLiabilityClosing":
      return "leaseLiability";
    default:
      return key;
  }
}

// ─── SortableHeader ────────────────────────────────────────────────────────

interface SortableHeaderProps {
  sortKey: SortKey;
  currentSort: SortKey;
  dir: SortDir;
  onClick: (key: SortKey) => void;
  label: string;
  alignRight?: boolean;
}

function SortableHeader({
  sortKey,
  currentSort,
  dir,
  onClick,
  label,
  alignRight,
}: SortableHeaderProps) {
  const active = currentSort === sortKey;
  return (
    <TableHead
      className={cn(
        "cursor-pointer select-none hover:bg-muted/30",
        alignRight && "text-right",
      )}
      onClick={() => onClick(sortKey)}
      data-testid={`contracts-header-${sortKey}`}
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <span
        className={cn(
          "inline-flex items-center gap-1",
          alignRight && "justify-end w-full",
        )}
      >
        {label}
        {active &&
          (dir === "asc" ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          ))}
      </span>
    </TableHead>
  );
}

// ─── ContractRow ───────────────────────────────────────────────────────────

interface ContractRowProps {
  contract: ContractSummary;
  visibleColumns: Record<ColumnKey, boolean>;
  currency: string;
  /**
   * Phase 6a.18 — threaded through so the Detay button can navigate to
   * the per-contract detail page (`/engagements/:eid/contracts/:cid`).
   */
  engagementId: string;
}

function ContractRow({
  contract,
  visibleColumns,
  currency,
  engagementId,
}: ContractRowProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const commencement = parseISO(contract.commencementDate);
  const commencementDisplay = Number.isNaN(commencement.getTime())
    ? contract.commencementDate
    : format(commencement, "dd.MM.yyyy");

  // Phase 6a.18b — single source of truth for the per-contract URL.
  // The helper enforces both the Phase 6a.18b1 `/results/` segment and
  // the uniform URI encoding of both path components.
  const detailPath = buildContractDetailPath(engagementId, contract.contractId);

  function handleDetailClick() {
    navigate(detailPath);
  }

  return (
    <TableRow data-testid={`contract-row-${contract.contractId}`}>
      {visibleColumns.index && (
        <TableCell className="font-mono">{contract.contractIndex}</TableCell>
      )}
      {visibleColumns.contractId && (
        <TableCell className="font-medium">
          {/* Phase 6a.18b — the contractId cell is itself a link to the
              detail page, in addition to the dedicated Detay button.
              Auditors who scan the grid by contractId text don't have
              to hunt for the action column. */}
          <Link
            to={detailPath}
            className="hover:underline"
            data-testid={`contract-id-link-${contract.contractId}`}
          >
            {contract.contractId}
          </Link>
        </TableCell>
      )}
      {visibleColumns.description && (
        <TableCell
          className="max-w-[200px] truncate"
          title={contract.description ?? ""}
        >
          {contract.description ?? ""}
        </TableCell>
      )}
      {visibleColumns.assetClass && (
        <TableCell>
          {t(`form.assetClass.${contract.assetClass}` as never) as string}
        </TableCell>
      )}
      {visibleColumns.commencementDate && (
        <TableCell>{commencementDisplay}</TableCell>
      )}
      {visibleColumns.termMonths && (
        <TableCell>
          {t("contracts.value.termMonths", { count: contract.termMonths })}
        </TableCell>
      )}
      {visibleColumns.currency && (
        <TableCell className="font-mono">{contract.currency}</TableCell>
      )}
      {visibleColumns.rouAssetClosing && (
        <TableCell className="text-right font-mono tabular-nums">
          {formatCurrency(contract.rouAssetClosing, currency)}
        </TableCell>
      )}
      {visibleColumns.leaseLiabilityClosing && (
        <TableCell className="text-right font-mono tabular-nums">
          {formatCurrency(contract.leaseLiabilityClosing, currency)}
        </TableCell>
      )}
      {visibleColumns.flags && (
        <TableCell>
          <FlagBadges contract={contract} />
        </TableCell>
      )}
      {visibleColumns.detail && (
        <TableCell className="w-10">
          {/* Phase BB5-7 — icon-only button + tooltip.
              Phase BB6-6 — promoted ghost → outline + sm sizing + larger
              ExternalLink icon so the button reads as an action rather
              than a passive glyph. */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDetailClick}
                  aria-label={t("contracts.detail.tooltip")}
                  data-testid={`contract-detail-${contract.contractId}`}
                >
                  <ExternalLink size={16} aria-hidden="true" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t("contracts.detail.tooltip")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </TableCell>
      )}
    </TableRow>
  );
}

// ─── FlagBadges ────────────────────────────────────────────────────────────

interface FlagBadgesProps {
  contract: ContractSummary;
}

function FlagBadges({ contract }: FlagBadgesProps) {
  const { t } = useTranslation();
  const flags = [
    {
      active: contract.isMultiCurrency,
      emoji: "🌐",
      label: t("contracts.flag.multiCurrency"),
      key: "multiCurrency",
    },
    {
      active: contract.hasModifications,
      emoji: "✏️",
      label: t("contracts.flag.modification"),
      key: "modification",
    },
    {
      active: contract.hasTermination,
      emoji: "⏹",
      label: t("contracts.flag.termination"),
      key: "termination",
    },
    {
      active: contract.hasIndexLinkage,
      emoji: "📊",
      label: t("contracts.flag.indexLinkage"),
      key: "indexLinkage",
    },
    {
      active: contract.hasPriorReconciliation,
      emoji: "🔄",
      label: t("contracts.flag.priorReconciliation"),
      key: "priorReconciliation",
    },
  ];
  const active = flags.filter((f) => f.active);
  if (active.length === 0) {
    return (
      <span
        className="text-muted-foreground"
        title={t("contracts.flag.none")}
        data-testid={`contract-flags-${contract.contractId}-none`}
      >
        —
      </span>
    );
  }
  return (
    <div
      className="flex gap-1 items-center"
      data-testid={`contract-flags-${contract.contractId}`}
    >
      {active.map((f) => (
        <span
          key={f.key}
          title={f.label}
          data-testid={`contract-flag-${contract.contractId}-${f.key}`}
        >
          {f.emoji}
        </span>
      ))}
    </div>
  );
}
